/**
 * Transient node highlight for AI edits. When a graph-op batch lands, the
 * affected nodes briefly pulse on canvas so the user's eye is drawn to what
 * changed. Best-effort and side-effect only — reuses the shared history-store
 * React Flow accessors (same ones Cmd+Z uses), so it needs no editor wiring.
 */
import { useHistoryStore } from '@/features/backtest/stores/history-store'
import type { Node } from '@xyflow/react'

export const PULSE_CLASS = 'ai-edit-pulse'
export const PULSE_MS = 1600

function withClass(node: Node, add: boolean): Node {
  const classes = new Set((node.className ?? '').split(/\s+/).filter(Boolean))
  if (add) classes.add(PULSE_CLASS)
  else classes.delete(PULSE_CLASS)
  return { ...node, className: [...classes].join(' ') }
}

/**
 * Add {@link PULSE_CLASS} to the given node ids, then remove it after
 * {@link PULSE_MS}. No-op when no editor history is configured. Deferred one
 * tick so the batch commit has synced into the React Flow store first.
 */
export function pulseNodes(nodeIds: string[]): void {
  if (nodeIds.length === 0) return
  const ids = new Set(nodeIds)

  const apply = (add: boolean) => {
    const acc = useHistoryStore.getState().accessors
    if (!acc) return
    acc.setNodes(acc.getNodes().map((n) => (ids.has(n.id) ? withClass(n, add) : n)))
  }

  const addTimer = setTimeout(() => apply(true), 30)
  setTimeout(() => {
    clearTimeout(addTimer)
    apply(false)
  }, PULSE_MS)
}
